import React, { useState, useCallback, useRef } from 'react';
import { ChevronRight, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Finetools-style vertical category menu.
 * - Hover a parent → subcategories fly out to the RIGHT
 * - Click a parent → navigate/filter to that category
 * - Mouse gap bridge: invisible padding prevents flyout from closing during cursor travel
 */
const CategorySidebar = ({ categories, onCategoryChange, categoryId }) => {
    const [hoveredCat, setHoveredCat] = useState(null);
    const navigate = useNavigate();
    const leaveTimerRef = useRef(null);

    const handleCatClick = useCallback((cat) => {
        if (onCategoryChange) onCategoryChange(cat.id);
        else navigate(`/products?category=${cat.id}`);
    }, [onCategoryChange, navigate]);

    const handleAllClick = useCallback(() => {
        if (onCategoryChange) onCategoryChange(null);
        else navigate('/products');
    }, [onCategoryChange, navigate]);

    // Debounced mouse leave to prevent flyout from closing during cursor travel
    const handleMouseEnter = (catId) => {
        if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        setHoveredCat(catId);
    };

    const handleMouseLeave = () => {
        leaveTimerRef.current = setTimeout(() => setHoveredCat(null), 120);
    };

    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 h-full relative">
            {/* Blue header bar */}
            <div className="bg-[#0c1b44] text-white px-4 py-[14px] flex items-center gap-3 rounded-t-lg">
                <Menu className="h-5 w-5 flex-shrink-0" />
                <h2 className="text-sm font-bold uppercase tracking-widest">All Categories</h2>
            </div>

            {/* Category list */}
            <div className="relative">
                {/* All Products row */}
                <button
                    onClick={handleAllClick}
                    className={`w-full text-left px-4 py-3 text-sm font-semibold border-b border-gray-100 flex items-center transition-colors ${
                        !categoryId
                            ? 'bg-[#0c1b44] text-white'
                            : 'text-[#0c1b44] bg-gray-50 hover:bg-[#0c1b44] hover:text-white'
                    }`}
                >
                    All Products
                </button>

                {/* Parent categories */}
                {categories.map((cat) => {
                    const isActive = categoryId === cat.id ||
                        (cat.children && cat.children.some(c => c.id === categoryId));

                    return (
                        <div
                            key={cat.id}
                            className="relative group"
                            onMouseEnter={() => handleMouseEnter(cat.id)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                onClick={() => handleCatClick(cat)}
                                className={`w-full text-left px-4 py-3 text-sm font-medium border-b border-gray-100 flex items-center justify-between transition-colors ${
                                    isActive || hoveredCat === cat.id
                                        ? 'bg-[#0c1b44] text-white'
                                        : 'text-gray-700 hover:bg-[#0c1b44] hover:text-white'
                                }`}
                            >
                                <span className="truncate pr-2 flex-1 min-w-0 text-left">{cat.name}</span>
                                <div className="flex items-center flex-shrink-0 gap-0">
                                    {/* Fixed-width count slot — always right-aligned, tabular nums */}
                                    <span className={`text-xs w-12 text-right tabular-nums ${
                                        isActive || hoveredCat === cat.id ? 'text-white/60' : 'text-gray-400'
                                    }`}>
                                        ({cat.total_product_count || cat.product_count || 0})
                                    </span>
                                    {/* Fixed-width arrow slot — always same width, arrow or blank */}
                                    <span className="w-5 flex items-center justify-center flex-shrink-0">
                                        {cat.children && cat.children.length > 0 ? (
                                            <ChevronRight className={`h-3.5 w-3.5 ${
                                                isActive || hoveredCat === cat.id ? 'text-white' : 'text-gray-400'
                                            }`} />
                                        ) : null}
                                    </span>
                                </div>
                            </button>

                            {/* Subcategory flyout panel — with invisible bridge gap to prevent close */}
                            {cat.children && cat.children.length > 0 && hoveredCat === cat.id && (
                                <>
                                    {/* Invisible bridge: covers the gap between sidebar and flyout */}
                                    <div
                                        className="absolute top-0 left-full h-full w-3 z-40"
                                        onMouseEnter={() => handleMouseEnter(cat.id)}
                                        onMouseLeave={handleMouseLeave}
                                    />
                                    <div
                                        className="absolute left-full top-0 z-50 bg-white border border-gray-200 rounded-r-lg shadow-xl"
                                        style={{ minWidth: 240, marginLeft: '0px' }}
                                        onMouseEnter={() => handleMouseEnter(cat.id)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        {/* Flyout header */}
                                        <div className="bg-[#0c1b44] px-4 py-3 rounded-tr-lg">
                                            <p className="text-xs font-bold text-white uppercase tracking-wider">{cat.name}</p>
                                            <p className="text-white/60 text-[10px] mt-0.5">{cat.children.length} subcategories</p>
                                        </div>
                                        <div className="py-1 max-h-80 overflow-y-auto">
                                            {/* "All [category]" option at top */}
                                            <button
                                                onClick={() => {
                                                    handleCatClick(cat);
                                                    setHoveredCat(null);
                                                }}
                                                className={`w-full text-left px-4 py-2.5 text-sm font-semibold border-b border-gray-100 flex items-center justify-between transition-colors ${
                                                    categoryId === cat.id
                                                        ? 'bg-[#e8edf5] text-[#0c1b44]'
                                                        : 'text-[#0c1b44] hover:bg-[#0c1b44] hover:text-white'
                                                }`}
                                            >
                                                <span className="truncate pr-2 flex-1 min-w-0 text-left">All {cat.name}</span>
                                                <span className={`text-[10px] flex-shrink-0 ml-2 ${
                                                    categoryId === cat.id ? 'text-[#0c1b44]/60' : 'text-gray-400'
                                                }`}>
                                                    ({cat.total_product_count || cat.product_count || 0})
                                                </span>
                                            </button>
                                            {cat.children.map((sub) => (
                                                <button
                                                    key={sub.id}
                                                    onClick={() => {
                                                        if (onCategoryChange) onCategoryChange(sub.id);
                                                        else navigate(`/products?category=${sub.id}`);
                                                        setHoveredCat(null);
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm border-b border-gray-50 last:border-0 flex items-center justify-between transition-colors ${
                                                        categoryId === sub.id
                                                            ? 'bg-[#e8edf5] text-[#0c1b44] font-semibold'
                                                            : 'text-gray-700 hover:bg-[#0c1b44] hover:text-white'
                                                    }`}
                                                >
                                                    <span className="truncate pr-2 flex-1 min-w-0 text-left">{sub.name}</span>
                                                    {(sub.product_count > 0 || sub.product_count === 0) && (
                                                        <span className={`text-[10px] ml-2 flex-shrink-0 ${
                                                            categoryId === sub.id ? 'text-[#0c1b44]/60' : 'text-gray-400'
                                                        }`}>
                                                            ({sub.product_count ?? 0})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategorySidebar;
